import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LinkifyText from '../components/LinkifyText';
import SmartMentionsInput from '../components/SmartMentionsInput';
import NextcloudTalk from '../components/NextcloudTalk';
import {
  Table, Button, Modal, Form, Input, InputNumber, Select, Tag, Space, message,
  Card, Descriptions, Divider, List, Typography, DatePicker, Tabs, Tooltip,
  Popconfirm, Avatar, Alert, Steps, Progress, Collapse,
} from 'antd';
import {
  PlusOutlined, VideoCameraOutlined, ArrowLeftOutlined,
  DeleteOutlined, FileTextOutlined, BulbOutlined,
  UserOutlined, TeamOutlined, CalendarOutlined,
  RobotOutlined, LinkOutlined, PlayCircleOutlined,
  PauseCircleOutlined, AudioOutlined, StopOutlined,
  CheckCircleOutlined, ClockCircleOutlined, ProjectOutlined,
  ShareAltOutlined, EditOutlined, InboxOutlined, UndoOutlined,
} from '@ant-design/icons';
import api from '../api/client';
import dayjs from 'dayjs';
import { TasksService } from '../api/services/tasks.service';
import { UsersService } from '../api/services/users.service';
import { MeetingsService } from '../api/services/meetings.service';
import { ProjectsService } from '../api/services/projects.service';
import { useFileViewer } from '../store/FileViewerContext';
import 'dayjs/locale/ru';

dayjs.locale('ru');

import { useAuth } from '../store/auth';

const { Text, Title } = Typography;
const { TextArea } = Input;

export default function MeetingsPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { openFileCard } = useFileViewer();
  const { user } = useAuth();

  const canManageMeetings = user?.special_role === 'secretary' || user?.role_level >= 5 || user?.username === 'khaminov';

  const [meetings, setMeetings] = useState([]);
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [agendaModalOpen, setAgendaModalOpen] = useState(false);
  const [showJitsiModal, setShowJitsiModal] = useState(false);
  const [form] = Form.useForm();
  const [agendaForm] = Form.useForm();

  const [allUsers, setAllUsers] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  // ─── meeting in-progress state ────────────────────────────
  const [inProgress, setInProgress] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // ─── data fetching ────────────────────────────────────────

  const fetchMeetings = useCallback(() => {
    MeetingsService.getMeetings(showArchived).then(r => setMeetings(r.data)).catch(() => {});
  }, [showArchived]);

  const fetchMeeting = useCallback((id) => {
    setLoading(true);
    MeetingsService.getMeetingById(id)
      .then(r => setMeeting(r.data))
      .catch(() => message.error('Совещание не найдено'))
      .finally(() => setLoading(false));
  }, []);

  const fetchUsers = useCallback(() => {
    UsersService.getUsers().then(r => setAllUsers(r.data)).catch(() => {});
  }, []);

  const fetchTasks = useCallback(() => {
    TasksService.getTasks().then(r => setAllTasks(r.data)).catch(() => {});
  }, []);

  const fetchProjects = useCallback(() => {
    ProjectsService.getProjects().then(r => setAllProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchUsers(); fetchProjects(); fetchTasks(); }, []);

  useEffect(() => {
    if (meetingId) {
      fetchMeeting(meetingId);
    } else {
      setMeeting(null);
      fetchMeetings();
    }
  }, [meetingId]);

  // ─── duration auto-calc ───────────────────────────────────

  const handleFormValuesChange = (changed, allValues) => {
    if (changed.duration !== undefined && allValues.start_time) {
      const end = allValues.start_time.add(changed.duration, 'minute');
      form.setFieldValue('end_time', end);
    } else if (changed.start_time && allValues.duration) {
      const end = changed.start_time.add(allValues.duration, 'minute');
      form.setFieldValue('end_time', end);
    } else if (changed.end_time && allValues.start_time) {
      const dur = changed.end_time.diff(allValues.start_time, 'minute');
      if (dur > 0) form.setFieldValue('duration', dur);
    }
  };

  // ─── create meeting ───────────────────────────────────────

  const handleCreate = async (values) => {
    try {
      const payload = {
        title: values.title,
        description: values.description || null,
        start_time: values.start_time?.toISOString() || null,
        end_time: values.end_time?.toISOString() || null,
        location: values.location || null,
        participant_ids: values.participant_ids || [],
        secretary_id: values.secretary_id || null,
        external_participants: values.external_participants || [],
        task_ids: values.task_ids || [],
        project_ids: values.project_ids || [],
      };
      await MeetingsService.createMeeting(payload);
      message.success('Совещание создано');
      setModalOpen(false);
      form.resetFields();
      fetchMeetings();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка создания');
    }
  };

  const handleUpdate = async (values) => {
    try {
      const payload = {
        title: values.title,
        description: values.description || null,
        start_time: values.start_time?.toISOString() || null,
        end_time: values.end_time?.toISOString() || null,
        location: values.location || null,
        participant_ids: values.participant_ids || [],
        secretary_id: values.secretary_id || null,
        task_ids: values.task_ids || [],
        project_ids: values.project_ids || [],
      };
      await MeetingsService.updateMeeting(meetingId, payload);
      message.success('Совещание обновлено');
      setModalOpen(false);
      setEditMode(false);
      form.resetFields();
      fetchMeeting(meetingId);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const handleArchiveMeeting = async (meetingId) => {
    try {
      await MeetingsService.archiveMeeting(meetingId);
      message.success('Совещание архивировано');
      fetchMeetings();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка архивирования');
    }
  };

  const handleUnarchiveMeeting = async (meetingId) => {
    try {
      await MeetingsService.unarchiveMeeting(meetingId);
      message.success('Совещание восстановлено');
      fetchMeetings();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка восстановления');
    }
  };

  const handleEditMeetingClick = () => {
    setEditMode(true);
    form.setFieldsValue({
      title: meeting.title,
      description: meeting.description,
      start_time: meeting.scheduled_start ? dayjs(meeting.scheduled_start) : null,
      end_time: meeting.scheduled_end ? dayjs(meeting.scheduled_end) : null,
      duration: (meeting.scheduled_start && meeting.scheduled_end) ? dayjs(meeting.scheduled_end).diff(dayjs(meeting.scheduled_start), 'minute') : null,
      location: meeting.location,
      participant_ids: meeting.participants?.map(p => p.user_id) || [],
      secretary_id: meeting.secretary_id,
      task_ids: meeting.linked_tasks?.map(t => t.id) || [],
      project_ids: meeting.linked_projects?.map(p => p.id) || [],
    });
    setModalOpen(true);
  };

  // ─── agenda ───────────────────────────────────────────────

  const handleAddAgenda = async (values) => {
    try {
      await MeetingsService.addAgenda(meetingId, {
        task_id: values.task_id,
        sort_order: values.sort_order || 0,
      });
      message.success('Вопрос добавлен в повестку');
      setAgendaModalOpen(false);
      agendaForm.resetFields();
      fetchMeeting(meetingId);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteAgenda = async (itemId) => {
    await MeetingsService.deleteAgenda(meetingId, itemId);
    message.success('Вопрос удалён');
    fetchMeeting(meetingId);
  };

  // ─── protocol & decisions save ────────────────────────────

  const saveProtocol = async (value) => {
    await MeetingsService.updateNotes(meetingId, { protocol: value });
    message.success('Протокол сохранён');
  };

  const saveDecisions = async (value) => {
    await MeetingsService.updateNotes(meetingId, { decisions: value });
    message.success('Решения сохранены');
  };

  const handleSaveAgendaDecision = async (itemId, text, publish = false) => {
    try {
      await api.patch(`/meetings/${meetingId}/agenda/${itemId}`, {
        decision_text: text,
        is_decision_published: publish
      });
      if (publish) message.success('Решение опубликовано');
      fetchMeeting(meetingId);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка сохранения решения');
    }
  };

  // ─── start / stop meeting ──────────────────────────────────

  const handleStartMeeting = async () => {
    try {
      const res = await MeetingsService.startMeeting(meetingId);
      setMeeting(res.data);
      setInProgress(true);
      message.success('Совещание начато');
      // Auto-start recording
      startRecording();
    } catch (e) {
      if (e.response?.data?.detail === 'Meeting already started') {
        // Already started — just enter in-progress mode
        setInProgress(true);
        startRecording();
      } else {
        message.error(e.response?.data?.detail || 'Ошибка запуска');
      }
    }
  };

  const startRecording = async () => {
    try {
      // 1. Захват системного аудио (вкладки/экрана)
      const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      // Отключаем видео, так как нам нужен только звук
      displayStream.getVideoTracks().forEach(track => track.stop());

      // 2. Захват микрофона пользователя
      const userStream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // 3. Микширование потоков через Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const dest = audioContext.createMediaStreamDestination();

      // Подключаем системный звук (если есть аудиотреки)
      if (displayStream.getAudioTracks().length > 0) {
        const displaySource = audioContext.createMediaStreamSource(displayStream);
        displaySource.connect(dest);
      }

      // Подключаем звук микрофона
      if (userStream.getAudioTracks().length > 0) {
        const userSource = audioContext.createMediaStreamSource(userStream);
        userSource.connect(dest);
      }

      const mixedStream = dest.stream;

      streamRef.current = {
        getTracks: () => [...displayStream.getTracks(), ...userStream.getTracks()],
        audioContext: audioContext
      };

      const mediaRecorder = new MediaRecorder(mixedStream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.start(1000); // collect chunks every second
      setRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      message.success('Запись начата');
    } catch (err) {
      console.error('Recording error:', err);
      message.warning('Не удалось получить доступ к экрану или микрофону. Совещание продолжится без записи.');
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      const mr = mediaRecorderRef.current;
      if (!mr || mr.state === 'inactive') {
        resolve(null);
        return;
      }

      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioChunksRef.current = [];
        resolve(blob);
      };
      mr.stop();

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        if (streamRef.current.audioContext) {
          streamRef.current.audioContext.close();
        }
        streamRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecording(false);
    });
  };

  const handleEndMeeting = async () => {
    try {
      // Stop recording and get blob
      const audioBlob = await stopRecording();

      // End meeting on server
      const endRes = await MeetingsService.endMeeting(meetingId);
      setMeeting(endRes.data);

      // Upload recording if we have one
      if (audioBlob && audioBlob.size > 0) {
        const formData = new FormData();
        formData.append('file', audioBlob, `recording_${meetingId}.webm`);
        message.loading({ content: 'Сохранение записи...', key: 'upload' });

        const uploadRes = await MeetingsService.uploadRecording(
          meetingId,
          formData
        );
        setMeeting(uploadRes.data);
        message.success({ content: 'Запись сохранена в Топы/Совещания', key: 'upload' });
      }

      setInProgress(false);
      message.success('Совещание завершено');
      fetchMeeting(meetingId);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка завершения');
    }
  };

  const formatRecordingTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ─── helpers ──────────────────────────────────────────────

  const userOptions = allUsers.map(u => ({ value: u.id, label: u.full_name }));

  const formatDuration = (start, end) => {
    if (!start || !end) return '—';
    const mins = dayjs(end).diff(dayjs(start), 'minute');
    if (mins < 60) return `${mins} мин`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
  };

  const meetingModal = (
    <Modal title={editMode ? "Редактировать совещание" : "Новое совещание"} open={modalOpen} onCancel={() => { setModalOpen(false); setEditMode(false); }} onOk={() => form.submit()} okText={editMode ? "Сохранить" : "Создать"} width={{ xs: '100%', sm: 640 }} style={{ top: 20 }} forceRender>
      <Form form={form} layout="vertical" onFinish={editMode ? handleUpdate : handleCreate} onValuesChange={handleFormValuesChange}>
        <Form.Item name="title" label="Тема совещания" rules={[{ required: true, message: 'Введите тему' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Описание">
          <SmartMentionsInput rows={3} />
        </Form.Item>
        <Space style={{ width: '100%' }} size="middle">
          <Form.Item name="start_time" label="Дата и время начала" rules={[{ required: true, message: 'Укажите начало' }]} style={{ flex: 1 }}>
            <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="duration" label="Продолжительность (мин)">
            <InputNumber min={5} max={480} step={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end_time" label="Время окончания" rules={[{ required: true, message: 'Укажите конец' }]} style={{ flex: 1 }}>
            <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </Space>
        <Form.Item name="location" label="Место проведения">
          <Input />
        </Form.Item>
        <Form.Item name="participant_ids" label="Участники">
          <Select mode="multiple" placeholder="Выберите участников" showSearch optionFilterProp="label" options={userOptions} />
        </Form.Item>
        <Form.Item name="secretary_id" label="Секретарь">
          <Select placeholder="Выберите секретаря" showSearch optionFilterProp="label" options={userOptions} allowClear />
        </Form.Item>
        <Form.Item name="project_ids" label="Связанные проекты">
          <Select mode="multiple" placeholder="Выберите проекты" showSearch optionFilterProp="label" options={allProjects.map(p => ({value: p.id, label: p.name}))} />
        </Form.Item>
        <Form.Item name="task_ids" label="Связанные задачи">
          <Select mode="multiple" placeholder="Выберите задачи" showSearch optionFilterProp="label" options={allTasks.map(t => ({value: t.id, label: t.title}))} />
        </Form.Item>
      </Form>
    </Modal>
  );

  // ─── TABLE VIEW ───────────────────────────────────────────

  if (!meetingId) {
    const columns = [
      {
        title: 'Тема', dataIndex: 'title', key: 'title',
        render: (text, record) => (
          <a onClick={() => navigate(`/meetings/${record.id}`)}>{text}</a>
        ),
      },
      {
        title: 'Дата и время', key: 'datetime',
        render: (_, r) => r.scheduled_start ? dayjs(r.scheduled_start).format('DD.MM.YYYY HH:mm') : '—',
      },
      { title: 'Место', dataIndex: 'location', key: 'location', render: (v) => v || '—' },
      { title: 'Организатор', dataIndex: 'creator_name', key: 'creator_name', render: (v) => v || '—' },
      {
        title: 'Участники', dataIndex: 'participant_count', key: 'participants',
        render: (v) => <Tag icon={<TeamOutlined />}>{v}</Tag>,
      },
      {
        title: 'Статус', dataIndex: 'is_active', key: 'status',
        render: (active, record) => (
          <Space>
            {record.is_archived && <Tag color="default">Архив</Tag>}
            {active ? <Tag color="green">Активна</Tag> : <Tag>Завершена</Tag>}
          </Space>
        ),
      },
      {
        title: 'Ссылка', dataIndex: 'jitsi_url', key: 'link',
        render: (url) => <a href={url} target="_blank" rel="noopener noreferrer"><VideoCameraOutlined /> ВКС</a>,
      },
      {
        title: 'Действия',
        key: 'actions',
        render: (_, record) => canManageMeetings ? (
          <Space>
            {record.is_archived ? (
              <Popconfirm title="Восстановить совещание?" onConfirm={() => handleUnarchiveMeeting(record.id)}>
                <Button size="small" icon={<UndoOutlined />}>Восстановить</Button>
              </Popconfirm>
            ) : (
              <Popconfirm title="Архивировать совещание?" onConfirm={() => handleArchiveMeeting(record.id)}>
                <Button danger size="small" icon={<InboxOutlined />}>В архив</Button>
              </Popconfirm>
            )}
          </Space>
        ) : null,
      },
    ];

    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Совещания</h1>
          <p className="page-subtitle">Управление встречами и совещаниями</p>
        </div>
        
        <Space style={{ marginBottom: 16 }} wrap>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => { form.resetFields(); setEditMode(false); setModalOpen(true); }}
            style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
            }}
          >
            Новое совещание
          </Button>
          <Button icon={showArchived ? <InboxOutlined /> : <UndoOutlined />} onClick={() => setShowArchived(!showArchived)}>
            {showArchived ? 'Скрыть архивные' : 'Показать архивные'}
          </Button>
        </Space>
        <Card style={{ borderRadius: 16 }} styles={{ body: { padding: 0 } }}>
          <Table dataSource={meetings} columns={columns} rowKey="id" scroll={{ x: 'max-content' }} size="small" />
        </Card>

        {meetingModal}
      </div>
    );
  }

  // ─── DETAIL VIEW ──────────────────────────────────────────

  if (loading || !meeting) {
    return <div style={{ textAlign: 'center', padding: 48 }}>Загрузка...</div>;
  }

  const agendaGroups = (meeting?.agenda_items || []).reduce((acc, item) => {
    const author = item.added_by_name || 'Неизвестный';
    if (!acc[author]) acc[author] = [];
    acc[author].push(item);
    return acc;
  }, {});

  const tabItems = [
    {
      key: 'info',
      label: <span><CalendarOutlined /> Информация</span>,
      children: (
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Тема" span={2}>{meeting.title}</Descriptions.Item>
          <Descriptions.Item label="Описание" span={2}>{meeting.description ? <LinkifyText text={meeting.description} /> : '—'}</Descriptions.Item>
          <Descriptions.Item label="Дата начала">
            {meeting.scheduled_start ? dayjs(meeting.scheduled_start).format('DD.MM.YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Время начала">
            {meeting.scheduled_start ? dayjs(meeting.scheduled_start).format('HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Продолжительность">
            {formatDuration(meeting.scheduled_start, meeting.scheduled_end)}
          </Descriptions.Item>
          <Descriptions.Item label="Время окончания">
            {meeting.scheduled_end ? dayjs(meeting.scheduled_end).format('HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Место проведения" span={2}>{meeting.location || '—'}</Descriptions.Item>
          <Descriptions.Item label="Организатор">{meeting.creator_name || '—'}</Descriptions.Item>
          <Descriptions.Item label="Секретарь">{meeting.secretary_name || '—'}</Descriptions.Item>
          <Descriptions.Item label="Статус" span={2}>
            {meeting.is_active ? <Tag color="green">Активна</Tag> : <Tag>Завершена</Tag>}
          </Descriptions.Item>
          {meeting.linked_projects?.length > 0 && (
            <Descriptions.Item label="Связанные проекты" span={2}>
              <Space wrap>
                {meeting.linked_projects.map(p => (
                  <Tag icon={<ProjectOutlined />} color="geekblue" key={p.id}>
                    <a onClick={() => navigate(`/projects/${p.id}`)}>{p.title}</a>
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          {meeting.linked_tasks?.length > 0 && (
            <Descriptions.Item label="Связанные задачи" span={2}>
              <Space wrap>
                {meeting.linked_tasks.map(t => (
                  <Tag icon={<LinkOutlined />} color="cyan" key={t.id}>
                    <a onClick={() => navigate(`/tasks?taskId=${t.id}`)}>{t.title}</a>
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          {meeting.jitsi_url && (
            <Descriptions.Item label="Видеоконференция" span={2}>
              <Button type="primary" icon={<VideoCameraOutlined />} onClick={() => setShowJitsiModal(true)}>
                Подключиться к ВКС
              </Button>
            </Descriptions.Item>
          )}
        </Descriptions>
      ),
    },
    {
      key: 'participants',
      label: <span><TeamOutlined /> Участники ({meeting.participants?.length || 0})</span>,
      children: (
        <div>
          <List
            dataSource={meeting.participants || []}
            locale={{ emptyText: 'Нет участников' }}
            renderItem={(p) => {
              let statusColor = 'default';
              let statusText = 'Ожидает';
              if (p.rsvp_status === 'accepted') { statusColor = 'green'; statusText = 'Принял'; }
              if (p.rsvp_status === 'declined') { statusColor = 'red'; statusText = 'Отклонил'; }
              
              return (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={p.full_name}
                    description={p.position || '—'}
                  />
                  {p.rsvp_status && <Tag color={statusColor}>{statusText}</Tag>}
                </List.Item>
              );
            }}
          />
          {meeting.external_participants?.length > 0 && (
            <>
              <Divider orientation="left">Внешние участники</Divider>
              <List
                dataSource={meeting.external_participants}
                renderItem={(ep) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} style={{ background: '#722ed1' }} />}
                      title={ep.name}
                      description={ep.email}
                    />
                  </List.Item>
                )}
              />
            </>
          )}
        </div>
      ),
    },
    {
      key: 'agenda',
      label: <span><FileTextOutlined /> Повестка ({meeting.agenda_items?.length || 0})</span>,
      children: (
        <div>
          <Button
            type="dashed" icon={<PlusOutlined />}
            onClick={() => { agendaForm.resetFields(); setAgendaModalOpen(true); }}
            style={{ marginBottom: 16 }}
          >
            Добавить вопрос
          </Button>

          {Object.keys(agendaGroups).length === 0 ? (
            <div style={{ color: '#8c8c8c', textAlign: 'center', padding: 24 }}>Повестка пуста</div>
          ) : (
            <Collapse defaultActiveKey={Object.keys(agendaGroups)}>
              {Object.entries(agendaGroups).map(([author, items]) => (
                <Collapse.Panel header={<Text strong><UserOutlined /> {author} ({items.length})</Text>} key={author}>
                  {items.map(item => (
                    <div key={item.id} style={{ marginBottom: 16, padding: 12, border: '1px solid #f0f0f0', borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <LinkOutlined style={{ fontSize: 16, color: '#1677ff', marginRight: 8 }} />
                          <Tooltip title={`Задача #${item.task_id}`}>
                            <a onClick={() => navigate(`/tasks`)} style={{ fontSize: 16, fontWeight: 500 }}>{item.task_title}</a>
                          </Tooltip>
                          <Space style={{ marginTop: 8, display: 'block' }}>
                            {item.project_name && <Tag color="geekblue">{item.project_name}</Tag>}
                            {item.due_date && <Tag color="orange">Срок: {dayjs(item.due_date).format('DD.MM.YYYY')}</Tag>}
                          </Space>
                        </div>
                        <Popconfirm title="Удалить вопрос?" onConfirm={() => handleDeleteAgenda(item.id)}>
                          <Button danger size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </div>

                      {/* Decision Block */}
                      <div style={{ marginTop: 12 }}>
                        {item.is_decision_published ? (
                          <Alert message="Решение опубликовано" description={<LinkifyText text={item.decision_text} />} type="success" showIcon />
                        ) : (
                          <div>
                            <Input.TextArea
                              defaultValue={item.decision_text}
                              placeholder="Решение по вопросу..."
                              autoSize={{ minRows: 2, maxRows: 6 }}
                              disabled={!canManageMeetings && user?.id !== meeting.secretary_id}
                              onBlur={(e) => {
                                if (canManageMeetings || user?.id === meeting.secretary_id) {
                                  handleSaveAgendaDecision(item.id, e.target.value, false);
                                }
                              }}
                              id={`agenda-decision-${item.id}`}
                            />
                            {(canManageMeetings || user?.id === meeting.secretary_id) && (
                              <Button
                                type="primary"
                                size="small"
                                style={{ marginTop: 8 }}
                                onClick={() => {
                                  const val = document.getElementById(`agenda-decision-${item.id}`).value;
                                  handleSaveAgendaDecision(item.id, val, true);
                                }}
                              >
                                Опубликовать
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          )}

          <Modal
            title="Добавить вопрос в повестку"
            open={agendaModalOpen}
            onCancel={() => setAgendaModalOpen(false)}
            onOk={() => agendaForm.submit()}
            okText="Добавить"
          >
            <Form form={agendaForm} layout="vertical" onFinish={handleAddAgenda}>
              <Form.Item name="task_id" label="Ссылка на задачу" rules={[{ required: true, message: 'Выберите задачу' }]}>
                <Select
                  placeholder="Выберите задачу"
                  showSearch
                  optionFilterProp="label"
                  options={allTasks.map(t => ({
                    value: t.id,
                    label: `${t.title}${t.project_id ? '' : ''}`,
                  }))}
                />
              </Form.Item>
              <Form.Item name="sort_order" label="Порядок" initialValue={0}>
                <InputNumber min={0} />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      ),
    },
    {
      key: 'protocol',
      label: <span><FileTextOutlined /> Протокол <Tag color="purple" style={{ marginLeft: 4 }}><RobotOutlined /> AI</Tag></span>,
      children: (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            В дальнейшем ИИ будет генерировать саммери из аудиозаписи совещания.
          </Text>
          <SmartMentionsInput
            rows={8}
            defaultValue={meeting.protocol || ''}
            placeholder="Введите протокол совещания..."
            onBlur={(e) => {
              if (e.target.value !== (meeting.protocol || '')) {
                saveProtocol(e.target.value);
              }
            }}
          />
        </div>
      ),
    },
    {
      key: 'decisions',
      label: <span><BulbOutlined /> Решения <Tag color="purple" style={{ marginLeft: 4 }}><RobotOutlined /> AI</Tag></span>,
      children: (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            В дальнейшем ИИ на основе протокола будет генерировать черновики задач.
          </Text>
          <SmartMentionsInput
            rows={8}
            defaultValue={meeting.decisions || ''}
            placeholder="Введите решения совещания..."
            onBlur={(e) => {
              if (e.target.value !== (meeting.decisions || '')) {
                saveDecisions(e.target.value);
              }
            }}
          />
        </div>
      ),
    },
  ];

  // Determine meeting status for display
  const meetingNotStarted = meeting.is_active && !meeting.started_at;
  const meetingRunning = meeting.is_active && meeting.started_at;
  const meetingEnded = !meeting.is_active;

  return (
    <div className="page-container">
      <div className="page-header">
        <Space style={{ marginBottom: 12 }} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/meetings')}>К списку</Button>
          <Button icon={<ShareAltOutlined />} onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/meetings/${meeting.id}`);
            message.success('Ссылка скопирована');
          }}>Скопировать ссылку</Button>
        </Space>
        <h1 className="page-title">{meeting.title}</h1>
        <Space wrap>
          {meeting.is_archived && <Tag color="default">Архив</Tag>}
          {meetingNotStarted && <Tag color="blue" icon={<ClockCircleOutlined />}>Запланировано</Tag>}
          {meetingRunning && <Tag color="green" icon={<PlayCircleOutlined />}>Идёт</Tag>}
          {meetingEnded && <Tag icon={<CheckCircleOutlined />}>Завершено</Tag>}
        </Space>
      </div>
      
      <Space style={{ marginBottom: 16 }} wrap>

        {(user?.id === meeting.creator_id || user?.id === meeting.secretary_id || canManageMeetings) && !meeting.regular_meeting_id && (
          <>
            <Button icon={<EditOutlined />} onClick={handleEditMeetingClick}>Редактировать</Button>
            {meeting.is_archived ? (
              <Popconfirm title="Восстановить совещание?" onConfirm={() => handleUnarchiveMeeting(meetingId)}>
                <Button icon={<UndoOutlined />}>Восстановить</Button>
              </Popconfirm>
            ) : (
              <Popconfirm title="Архивировать совещание?" onConfirm={() => handleArchiveMeeting(meetingId)}>
                <Button danger icon={<InboxOutlined />}>В архив</Button>
              </Popconfirm>
            )}
          </>
        )}

        {/* Start button — show only when not started and not in-progress mode */}
        {meetingNotStarted && !inProgress && (user?.id === meeting.secretary_id || canManageMeetings) && (
          <Popconfirm
            title="Начать совещание?"
            description="Будет включена аудиозапись. Убедитесь, что микрофон подключён."
            onConfirm={handleStartMeeting}
            okText="Начать"
            cancelText="Отмена"
          >
            <Button type="primary" icon={<PlayCircleOutlined />} size="large">
              Начать совещание
            </Button>
          </Popconfirm>
        )}

        {/* Resume button — if meeting was already started but user navigated away */}
        {meetingRunning && !inProgress && (user?.id === meeting.secretary_id || canManageMeetings) && (
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => { setInProgress(true); startRecording(); }}>
            Продолжить запись
          </Button>
        )}
      </Space>

      {/* ── In-progress panel ─── */}
      {inProgress && (
        <Card
          style={{ marginBottom: 16, borderColor: '#ff4d4f', borderWidth: 2 }}
          bodyStyle={{ padding: '16px 24px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <Space size="large">
              {recording && (
                <Space>
                  <span style={{
                    display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                    background: '#ff4d4f', animation: 'pulse 1.5s infinite',
                  }} />
                  <Text strong style={{ fontSize: 18, color: '#ff4d4f' }}>
                    <AudioOutlined /> REC {formatRecordingTime(recordingTime)}
                  </Text>
                </Space>
              )}
              <Text type="secondary">
                Совещание идёт с {meeting.started_at ? dayjs(meeting.started_at).format('HH:mm') : '—'}
              </Text>
            </Space>
            <Popconfirm
              title="Завершить совещание?"
              description="Запись будет сохранена в Топы/Совещания"
              onConfirm={handleEndMeeting}
              okText="Завершить"
              cancelText="Отмена"
            >
              {(user?.id === meeting.secretary_id || canManageMeetings) && (
                <Button danger type="primary" icon={<StopOutlined />} size="large">
                  Завершить совещание
                </Button>
              )}
            </Popconfirm>
          </div>

          {/* Agenda during meeting */}
          {meeting.agenda_items?.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Divider orientation="left" style={{ margin: '8px 0' }}>Повестка совещания</Divider>
              <Steps
                direction="vertical"
                size="small"
                current={-1}
                items={meeting.agenda_items.map((item, idx) => ({
                  title: (
                    <Space>
                      <Text strong>{item.task_title}</Text>
                      {item.project_name && <Tag color="geekblue" style={{ margin: 0 }}>{item.project_name}</Tag>}
                    </Space>
                  ),
                  description: item.due_date ? `Срок: ${dayjs(item.due_date).format('DD.MM.YYYY')}` : null,
                  icon: <FileTextOutlined />,
                }))}
              />
            </div>
          )}
        </Card>
      )}

      {/* Pulse animation for recording indicator */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
      `}</style>

      {/* Recording info for ended meetings */}
      {meetingEnded && meeting.recording_file_id && (
        <Alert
          type="success"
          showIcon
          icon={<AudioOutlined />}
          message="Запись совещания сохранена"
          description={
            <a onClick={() => openFileCard(meeting.recording_file_id)} style={{ cursor: 'pointer', color: '#1890ff' }}>
              Просмотр записи
            </a>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Tabs items={tabItems} defaultActiveKey={inProgress ? 'agenda' : 'info'} />
      </Card>

      <Modal
        title="Видеоконференция"
        open={showJitsiModal}
        onCancel={() => setShowJitsiModal(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        destroyOnClose
      >
        {showJitsiModal && (
          <div style={{ height: 'calc(100vh - 150px)' }}>
            <NextcloudTalk 
              roomName={meeting.room_id} 
              user={user}
              domain="https://10.11.1.250"
            />
          </div>
        )}
      </Modal>

      {meetingModal}
    </div>
  );
}
